package org.example.project.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import battleships.shared.generated.resources.Res
import battleships.shared.generated.resources.ocean
import kotlinx.coroutines.launch
import org.example.project.firebase.FirestoreManager
import org.example.project.model.User
import org.jetbrains.compose.resources.painterResource

@Composable
fun LeaderboardScreen(
    onBack: () -> Unit = {}
) {

    var leaderboard by remember { mutableStateOf<List<User>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }

    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        scope.launch {
            leaderboard = FirestoreManager.getLeaderboard()
            loading = false
        }
    }

    BoxWithConstraints(
        modifier = Modifier.fillMaxSize()
    ) {

        val compact = maxWidth < 700.dp

        Box {

            Image(
                painter = painterResource(Res.drawable.ocean),
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )

            Box(
                Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.55f))
            )

            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(
                        horizontal = if (compact) 16.dp else 40.dp,
                        vertical = 24.dp
                    )
            ) {

                if (compact) {

                    Text(
                        "GLOBAL LEADERBOARD",
                        color = Color.White,
                        fontWeight = FontWeight.ExtraBold,
                        fontSize = 24.sp
                    )

                    Text(
                        "TOP NAVAL COMMANDERS",
                        color = Color(0xFF00CFFF),
                        fontSize = 14.sp
                    )

                    Spacer(Modifier.height(18.dp))

                    Button(
                        modifier = Modifier.fillMaxWidth(),
                        onClick = onBack,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = Color(0xFF003B5C)
                        )
                    ) {
                        Text(
                            "RETURN TO COMMAND",
                            color = Color.White
                        )
                    }

                } else {

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {

                        Column {

                            Text(
                                "GLOBAL LEADERBOARD",
                                color = Color.White,
                                fontWeight = FontWeight.ExtraBold,
                                fontSize = 38.sp
                            )

                            Text(
                                "TOP NAVAL COMMANDERS",
                                color = Color(0xFF00CFFF),
                                fontSize = 18.sp
                            )
                        }

                        OutlinedButton(
                            onClick = onBack,
                            border = BorderStroke(
                                2.dp,
                                Color(0xFF00CFFF)
                            )
                        ) {

                            Text(
                                "RETURN",
                                color = Color(0xFF00CFFF)
                            )
                        }
                    }
                }

                Spacer(Modifier.height(26.dp))

                if (loading) {

                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {

                        CircularProgressIndicator(
                            color = Color(0xFF00CFFF)
                        )
                    }

                } else if (leaderboard.isEmpty()) {

                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {

                        Text(
                            "No Commanders Found",
                            color = Color.White,
                            fontSize = 20.sp
                        )
                    }

                } else {

                    LazyColumn(
                        verticalArrangement = Arrangement.spacedBy(18.dp),
                        contentPadding = PaddingValues(bottom = 40.dp)
                    ) {

                        itemsIndexed(leaderboard) { index, player ->

                            CommanderCard(
                                index = index,
                                player = player,
                                compact = compact
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CommanderCard(
    index: Int,
    player: User,
    compact: Boolean
) {

    val rankText = when (index) {
        0 -> "RANK #1"
        1 -> "RANK #2"
        2 -> "RANK #3"
        else -> "RANK #${index + 1}"
    }

    val borderColor = when (index) {
        0 -> Color(0xFFFFD54F)
        1 -> Color(0xFFE0E0E0)
        2 -> Color(0xFFFFB74D)
        else -> Color(0xFF00CFFF)
    }

    val backgroundColor = when (index) {
        0 -> Color(0xFF2A2105)
        1 -> Color(0xFF182330)
        2 -> Color(0xFF2A1B12)
        else -> Color(0xAA071420)
    }

    val winRate =
        if (player.gamesPlayed == 0)
            0
        else
            player.wins * 100 / player.gamesPlayed

    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .shadow(
                elevation = if (index == 0) 18.dp else 8.dp,
                shape = RoundedCornerShape(18.dp)
            ),
        color = backgroundColor,
        shape = RoundedCornerShape(18.dp),
        border = BorderStroke(
            if (index == 0) 3.dp else 2.dp,
            borderColor
        )
    ) {

        Column(
            modifier = Modifier.padding(
                horizontal = 24.dp,
                vertical = if (index == 0) 26.dp else 20.dp
            )
        ) {

            RankHeader(rankText, borderColor)

            Spacer(Modifier.height(20.dp))

            if (compact) {

                Text(
                    "COMMANDER",
                    color = Color.Gray,
                    fontSize = 12.sp
                )

                Text(
                    player.username,
                    color = Color.White,
                    fontSize = if (index == 0) 30.sp else 24.sp,
                    fontWeight = FontWeight.Bold
                )

                Spacer(Modifier.height(18.dp))

                LeaderboardStat("Score", player.score.toString())
                LeaderboardStat("Wins", player.wins.toString())
                LeaderboardStat("Losses", player.losses.toString())
                LeaderboardStat("Win Rate", "$winRate%")

            } else {

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Top
                ) {

                    Column(
                        modifier = Modifier.weight(1f)
                    ) {

                        Text(
                            "COMMANDER",
                            color = Color.Gray,
                            fontSize = 13.sp
                        )

                        Spacer(Modifier.height(6.dp))

                        Text(
                            player.username,
                            color = Color.White,
                            fontWeight = FontWeight.Bold,
                            fontSize = if (index == 0) 34.sp else 28.sp
                        )

                        Spacer(Modifier.height(8.dp))

                        Text(
                            "${player.gamesPlayed} Battles Fought",
                            color = Color.LightGray
                        )
                    }

                    Spacer(Modifier.width(40.dp))

                    Column(
                        modifier = Modifier.width(220.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {

                        DesktopStat("SCORE", player.score.toString())

                        DesktopStat("WINS", player.wins.toString())

                        DesktopStat("LOSSES", player.losses.toString())

                        DesktopStat("WIN RATE", "$winRate%")
                    }
                }
            }
        }
    }
}

@Composable
private fun LeaderboardStat(
    title: String,
    value: String
) {

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween
    ) {

        Text(
            title.uppercase(),
            color = Color.LightGray,
            fontWeight = FontWeight.Medium
        )

        Text(
            value,
            color = Color(0xFF00CFFF),
            fontWeight = FontWeight.Bold
        )
    }

    Spacer(Modifier.height(12.dp))
}

@Composable
private fun DesktopStat(
    title: String,
    value: String
) {

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween
    ) {

        Text(
            title,
            color = Color.Gray,
            fontSize = 14.sp
        )

        Text(
            value,
            color = Color(0xFF00CFFF),
            fontWeight = FontWeight.Bold,
            fontSize = 18.sp
        )
    }

    HorizontalDivider(
        modifier = Modifier.padding(top = 6.dp),
        color = Color.White.copy(alpha = 0.08f)
    )
}

@Composable
private fun RankHeader(
    text: String,
    color: Color
) {

    Surface(
        color = color.copy(alpha = 0.15f),
        border = BorderStroke(
            2.dp,
            color
        ),
        shape = RoundedCornerShape(50)
    ) {

        Text(
            text = text,
            modifier = Modifier.padding(
                horizontal = 18.dp,
                vertical = 8.dp
            ),
            color = color,
            fontWeight = FontWeight.Bold,
            fontSize = 15.sp
        )
    }
}